<template>
  <section class="section">
    <van-row type="flex" align="center">
      <van-col span="6">
        <div class="icon-wrap">
          <img :src="appData.icon" alt="" class="app-icon" />
        </div>
      </van-col>
      <van-col span="12">
        <h1 class="app-name">{{ t('donwload.appName') }}</h1>
        <p class="app-subtitle">{{ t('donwload.title') }}</p>
      </van-col>
      <van-col span="6">
        <div class="action-col">
          <van-button round size="small" color="#007AFF" class="get-btn" @click="getApp()">
            {{ t('appStore.action') }}
          </van-button>
          <p v-if="appData.hasInAppPurchases" class="iap-text">
            {{ t('appStore.inAppPurchases') }}
          </p>
        </div>
      </van-col>
    </van-row>

    <AppStoreInstallSelectionPopup
      ref="installPopup"
      @select="onInstallSelect"
    />
    <AppStorePwaInstructionsPopup ref="pwaInstructionsPopup" />
    <AppStoreIosProfileInstructionsPopup
      ref="iosProfileInstructionsPopup"
      @download="installIosProfile"
    />
  </section>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { showToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { appData } from '@/lib/app-data';
import { getAndroidDownloadURL } from '@/api/common';
import AppStoreInstallSelectionPopup from './AppStoreInstallSelectionPopup.vue';
import AppStorePwaInstructionsPopup from './AppStorePwaInstructionsPopup.vue';
import AppStoreIosProfileInstructionsPopup from './AppStoreIosProfileInstructionsPopup.vue';

const { t } = useI18n();

const installPopup = ref<InstanceType<typeof AppStoreInstallSelectionPopup> | null>(null);
const pwaInstructionsPopup = ref<InstanceType<typeof AppStorePwaInstructionsPopup> | null>(null);
const iosProfileInstructionsPopup = ref<InstanceType<typeof AppStoreIosProfileInstructionsPopup> | null>(null);
const deferredPrompt = ref<Event | null>(null);
const canInstall = ref(false);

const isIosSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || !!(window.navigator as Navigator & { standalone?: boolean }).standalone;
  return isIOS && isSafari && !isStandalone;
};

const isChromiumPwaSupported = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('chrome') || ua.includes('chromium') || ua.includes('edge');
};

const pwaSupported = computed(() => {
  if (isIosSafari()) {
    return true;
  }
  return canInstall.value
    && isChromiumPwaSupported()
    && !!deferredPrompt.value
    && 'prompt' in deferredPrompt.value;
});

const getApp = () => {
  installPopup.value?.open(pwaSupported.value);
};

const installPWA = () => {
  if (deferredPrompt.value && 'prompt' in deferredPrompt.value) {
    const promptEvent = deferredPrompt.value as Event & { prompt: () => void };
    promptEvent.prompt();
    deferredPrompt.value = null;
    return;
  }

  if (isIosSafari()) {
    pwaInstructionsPopup.value?.open();
  }
};

const normalizeDownloadUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

const openDownloadUrl = (downloadUrl: string) => {
  const url = normalizeDownloadUrl(downloadUrl);
  if (!url) {
    showToast(t('appStoreInstall.iosProfileFailed'));
    return;
  }

  if (isIosSafari()) {
    window.location.assign(url);
    return;
  }

  window.open(url, '_blank');
};

const installIosProfile = () => {
  getAndroidDownloadURL({ paramString: 'ios_download_url' }).then(res => {
    const downloadUrl = res.data?.ios_download_url || '';
    openDownloadUrl(downloadUrl);
  }).catch((err) => {
    console.log('ios profile install err: ', err);
    showToast(t('appStoreInstall.iosProfileFailed'));
  });
};

const onInstallSelect = (type: 'pwa' | 'iosProfile') => {
  if (type === 'pwa') {
    installPWA();
  } else {
    iosProfileInstructionsPopup.value?.open();
  }
};

window.addEventListener('beforeinstallprompt', (e: Event) => {
  e.preventDefault();
  deferredPrompt.value = e;
  canInstall.value = true;
});
</script>

<style scoped>
.section {
  padding: 8px 16px 0;
}

.icon-wrap {
  display: flex;
  justify-content: center;
}

.app-icon {
  width: 68px;
  height: 68px;
  border-radius: 15px;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.app-name {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.15;
  color: #111827;
}

.app-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.action-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.get-btn {
  min-width: 72px;
  font-weight: 600;
}

.iap-text {
  margin: 6px 0 0;
  font-size: 11px;
  color: #6b7280;
  text-align: right;
}
</style>
